/**
 * Pick observations the way Langfuse does: a source, the filter box and buttons,
 * then the matching observations to choose a sample from. Used by the configure
 * screen ("Sample from: Traces") and by the trace rule panel, where the filters
 * are what the rule saves.
 */

import { useId } from "react";
import { Link } from "react-router";
import { Option } from "@/components/ui/field";
import { Select } from "@/components/ui/select";
import { useTraceSources } from "./hooks";
import { ObservationFilters } from "./observation-filters";
import { ObservationTable } from "./observation-table";
import { NEW_QUERY, type TraceQuery } from "./trace-query";
import type { TraceRef } from "./types";

export function ObservationPicker({
  sourceId,
  onSource,
  locked = false,
  query,
  onQuery,
  selected,
  onSelect,
}: {
  sourceId: string;
  onSource: (id: string) => void;
  /** An existing rule keeps its source. */
  locked?: boolean;
  query: TraceQuery;
  onQuery: (q: TraceQuery) => void;
  /** `refKey` of the picked observation, or "". */
  selected: string;
  onSelect: (ref: TraceRef | null) => void;
}) {
  const id = useId();
  const { sources } = useTraceSources();
  const change = (q: TraceQuery) => (onQuery(q), onSelect(null));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1">
        <label htmlFor={id} className="text-xs font-semibold">
          Source
        </label>
        <Select
          id={id}
          buttonClassName="text-xs"
          value={sourceId}
          disabled={locked}
          onChange={(next) => (onSource(next), change(NEW_QUERY))}
          placeholder={sources.length ? "Choose a source…" : "No trace source yet"}
          options={sources.map((c) => ({ value: c.id, label: c.name }))}
        />
        {sources.length === 0 && (
          <span className="text-xs text-muted">
            <Link to="/settings/connections?add=1" className="underline">
              Connect one in Settings
            </Link>
            .
          </span>
        )}
      </div>
      {sourceId && (
        <>
          <ObservationFilters sourceId={sourceId} value={query} onChange={change} />
          <Option
            type="checkbox"
            checked={query.skipFailed}
            onChange={(e) => change({ ...query, skipFailed: e.target.checked })}
            note="A step that crashed has no answer to score."
          >
            Skip failed observations
          </Option>
          <ObservationTable sourceId={sourceId} query={query} selected={selected} onSelect={onSelect} />
        </>
      )}
    </div>
  );
}
